import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import AppointmentGroupModal from './AppointmentGroupModal';
import ChangeDoctorModal from './ChangeDoctorModal';
import DateInput from './DateInput';

const pad = (n) => String(n).padStart(2, '0');

const addDays = (dateStr, n) => {
    const d = new Date(`${dateStr}T00:00:00`);
    d.setDate(d.getDate() + n);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDayLabel = (dateStr) => {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString('sr-RS', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Podrazumevano radno vreme po danu u nedelji - mora se poklapati sa
// AppointmentService.defaultHoursFor na backendu. Važi kad za dati datum ne postoji
// eksplicitan override u /working-hours (videti WorkingHoursPanel).
const defaultHoursForDate = (dateStr) => {
    const dow = new Date(`${dateStr}T00:00:00`).getDay(); // 0=nedelja...6=subota
    if (dow === 6) return { startTime: '09:00', endTime: '20:00' }; // subota
    if (dow === 2) return { startTime: '11:00', endTime: '21:00' }; // utorak
    return { startTime: '14:00', endTime: '21:00' };
};

const SveskaView = () => {
    const navigate = useNavigate();
    const [allAppointments, setAllAppointments] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalAppointments, setModalAppointments] = useState(null);
    const [showChangeDoctor, setShowChangeDoctor] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [workingHours, setWorkingHours] = useState({}); // { 'YYYY-MM-DD': {startTime, endTime} }

    const [filters, setFilters] = useState({
        date: new Date().toISOString().split('T')[0],
        doctorId: '', // Uvek mora biti izabran konkretan doktor - nema više "Svi doktori"
        search: ''
    });

    const fetchData = async () => {
        try {
            const [resApp, resDocs] = await Promise.all([
                API.get('/appointments'),
                API.get('/doctors')
            ]);
            setAllAppointments(resApp.data);
            setDoctors(resDocs.data);
            // Ako još nije izabran doktor (prvi ulazak), podrazumevano izaberi prvog -
            // sprečava da Sveska ostane prazna dok korisnik sam ne izabere.
            setFilters(prev => prev.doctorId ? prev : { ...prev, doctorId: resDocs.data[0]?.id ? String(resDocs.data[0].id) : '' });
        } catch (err) {
            console.error("Greška:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Radno vreme ordinacije (isto za sve doktore) za oba prikazana dana (izabrani + sledeći) -
    // ako za neki dan ne postoji unos, ordinacija taj dan ne radi (nema termina uopšte).
    useEffect(() => {
        const startDate = filters.date;
        const endDate = addDays(filters.date, 1);

        API.get('/working-hours', { params: { startDate, endDate } })
            .then(res => {
                const map = {};
                res.data.forEach(w => { map[w.date] = w; });
                setWorkingHours(map);
            })
            .catch(() => setWorkingHours({}));
    }, [filters.date]);

    // Doktori podešeni da rade na izabrani dan (WorkingHoursPanel) - prazno/nepodešeno znači svi.
    const dayDoctorIds = workingHours[filters.date]?.doctorIds;
    const availableDoctors = dayDoctorIds && dayDoctorIds.length > 0
        ? doctors.filter(d => dayDoctorIds.some(id => String(id) === String(d.id)))
        : doctors;

    // Sveska uvek mora imati izabranog konkretnog doktora - ako izabrani doktor nije među
    // dozvoljenima za novi dan, prebaci na prvog dozvoljenog.
    useEffect(() => {
        if (!dayDoctorIds || dayDoctorIds.length === 0) return;
        if (!dayDoctorIds.some(id => String(id) === String(filters.doctorId))) {
            setFilters(prev => ({ ...prev, doctorId: String(dayDoctorIds[0]) }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dayDoctorIds, filters.date]);

    // Funkcija koja pravi listu slotova (na 15 min) unutar STVARNOG radnog vremena ordinacije
    // tog dana, za JEDAN dan (parametrizovano da bi se mogla pozvati i za "sledeći dan" kolonu).
    // Ako ne postoji eksplicitan override za taj datum, važi podrazumevani obrazac po danu u
    // nedelji. Override sa closed=true znači da je taj dan izričito zatvoren (npr. praznik) -
    // već zakazani termini se i tada i dalje prikazuju (ne sme da nestane postojeći termin).
    const generateFullDayGrid = (dateStr) => {
        const override = workingHours[dateStr];
        const timestamps = new Set();

        if (!override || !override.closed) {
            const dayHours = override || defaultHoursForDate(dateStr);
            const gridStart = new Date(`${dateStr}T${dayHours.startTime.substring(0, 5)}:00`);
            const gridEnd = new Date(`${dateStr}T${dayHours.endTime.substring(0, 5)}:00`);
            for (let t = gridStart.getTime(); t < gridEnd.getTime(); t += 15 * 60000) {
                timestamps.add(t);
            }
        }

        // Dodaj i stvarna vremena zakazanih termina tog dana - neki tretmani imaju trajanje
        // koje ne pada tačno na 15-minutni grid, pa termin ne sme da nestane iz sveske.
        allAppointments.forEach(app => {
            if (String(app.doctorId) === String(filters.doctorId) && app.startTime.startsWith(dateStr)) {
                timestamps.add(new Date(app.startTime).getTime());
            }
        });

        return Array.from(timestamps)
            .sort((a, b) => a - b)
            .map(slotTimestamp => {
                // Termini OVOG doktora čije trajanje pokriva ovaj slot - npr. termin u 15:20
                // koji traje 15 min zauzima doktora i u slotu 15:30.
                const overlapping = allAppointments.filter(app => {
                    if (String(app.doctorId) !== String(filters.doctorId)) return false;
                    const start = new Date(app.startTime).getTime();
                    const end = new Date(app.endTime).getTime();
                    return slotTimestamp >= start && slotTimestamp < end;
                });

                const appointments = overlapping.filter(app => new Date(app.startTime).getTime() === slotTimestamp);
                const occupied = appointments.length === 0 && overlapping.length > 0;

                return {
                    time: new Date(slotTimestamp).toISOString(),
                    appointments,
                    occupied
                };
            });
    };

    const formatTime = (isoString) => {
        return new Date(isoString).toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' });
    };

    // PDF sveske za izabrani dan - jedna strana po doktoru, za štampu i pečatiranje.
    const downloadPdf = async () => {
        setDownloadingPdf(true);
        try {
            const res = await API.get('/appointments/sveska-pdf', {
                params: { date: filters.date },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = `sveska-${filters.date}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert("Greška pri preuzimanju PDF-a.");
        } finally {
            setDownloadingPdf(false);
        }
    };

    // Prosleđuje doktora (uvek izabran) u grupno zakazivanje - tako se odmah predlaže isti
    // termin (datum, vreme, doktor) bez ponovnog kucanja. Koristi lokalne komponente vremena
    // (ne toISOString) da izbegne pomeranje datuma/vremena usled UTC konverzije.
    const buildScheduleLink = (slotTime) => {
        const d = new Date(slotTime);
        const localDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        const localTime = `${pad(d.getHours())}:${pad(d.getMinutes())}`;

        const params = new URLSearchParams({ date: localDate, startTime: localTime });
        if (filters.doctorId) params.set('doctorId', filters.doctorId);
        return `/group-scheduling?${params.toString()}`;
    };

    const goToDay = (offset) => {
        setFilters({ ...filters, date: addDays(filters.date, offset) });
    };

    // Klik na rezultat pretrage - skoči direktno na taj datum i doktora u Svesci
    // (kolone se prikazuju za taj dan) i očisti pretragu da se vidi normalan prikaz dana.
    const jumpToResult = (app) => {
        setFilters({
            date: app.startTime.split('T')[0],
            doctorId: String(app.doctorId),
            search: ''
        });
    };

    if (loading) return <div style={styles.loader}>Učitavanje...</div>;

    // Kontrole zakazane izabranog dana kod izabranog doktora - koristi "Promeni doktora".
    const kontrolaToday = allAppointments.filter(a =>
        String(a.doctorId) === String(filters.doctorId) &&
        a.startTime.startsWith(filters.date) &&
        a.serviceName === 'Kontrola'
    );
    const otherDoctorsToday = availableDoctors.filter(d => String(d.id) !== String(filters.doctorId));

    const searchTerm = filters.search.trim().toLowerCase();

    // Pretraga preko CELE sveske (svi doktori, svi budući datumi) - odvojeno od prikaza dve
    // kolone ispod, koje i dalje pokazuju samo izabrani dan/doktora. Samo od danas pa nadalje -
    // prošli termini nisu bitni za "gde je zakazana osoba".
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const globalSearchResults = searchTerm === '' ? [] : allAppointments
        .filter(app =>
            new Date(app.startTime) >= todayStart &&
            (app.patientFullName?.toLowerCase().includes(searchTerm) ||
                app.patientPhone?.includes(filters.search.trim()))
        )
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
        .slice(0, 15);

    const buildVisibleGrid = (dateStr) => {
        const fullGrid = generateFullDayGrid(dateStr);
        if (searchTerm === '') return fullGrid;
        return fullGrid.filter(slot =>
            slot.appointments.some(app =>
                app.patientFullName?.toLowerCase().includes(searchTerm) ||
                app.patientPhone?.includes(filters.search.trim())
            )
        );
    };

    const renderDayColumn = (dateStr, columnClassName) => {
        const visibleGrid = buildVisibleGrid(dateStr);
        // "Ne radi" znači izričito zatvoren dan (closed=true) BEZ ijednog zakazanog termina -
        // ne sme da se pomeša sa "pretraga bez rezultata" na dan kada ordinacija inače radi.
        const dayOff = workingHours[dateStr]?.closed && generateFullDayGrid(dateStr).length === 0;

        return (
            <div className={columnClassName} style={styles.column} key={dateStr}>
                <div style={styles.columnHeader}>{formatDayLabel(dateStr)}</div>
                <div style={styles.list}>
                    {dayOff && (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>
                            Ordinacija ne radi ovog dana.
                        </div>
                    )}
                    {!dayOff && searchTerm !== '' && visibleGrid.length === 0 && (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>
                            Nema termina za "{filters.search}" na ovaj dan.
                        </div>
                    )}
                    {visibleGrid.map((slot, index) => {
                        const count = slot.appointments.length;
                        const isGroup = count > 1;

                        return (
                            <div key={index} style={count > 0 ? styles.filledRow : styles.emptyRow}>
                                <div style={styles.timeLabel}>{formatTime(slot.time)}</div>

                                {count === 0 && slot.occupied && (
                                    <div style={styles.occupiedInfo}>
                                        Zauzeto (prethodni termin još traje)
                                    </div>
                                )}

                                {count === 0 && !slot.occupied && (
                                    <div
                                        style={styles.emptyInfo}
                                        onClick={() => navigate(buildScheduleLink(slot.time))}
                                    >
                                        <span style={styles.plus}>+</span> Slobodno
                                    </div>
                                )}

                                {count === 1 && (
                                    <div style={styles.patientInfo} onClick={() => setModalAppointments(slot.appointments)}>
                                        <div style={styles.pName}>{slot.appointments[0].patientFullName}</div>
                                        <div style={styles.pSub}>{slot.appointments[0].serviceName} • {slot.appointments[0].patientPhone}</div>
                                    </div>
                                )}

                                {isGroup && (
                                    <div style={styles.patientInfo} onClick={() => setModalAppointments(slot.appointments)}>
                                        <div style={styles.pName}>
                                            {slot.appointments[0].serviceName}
                                            <span style={styles.groupBadge}>{count}</span>
                                        </div>
                                        <div style={styles.pSub}>
                                            {slot.appointments.map(a => a.patientFullName).join(', ')}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div style={styles.container}>
            {/* Filter Panel */}
            <div style={styles.filterCard}>
                <div style={styles.rowInline}>
                    <button style={styles.pageBtn} onClick={() => goToDay(-1)}>‹ Prethodni dan</button>
                    <DateInput
                        style={styles.dateInput}
                        value={filters.date}
                        onChange={(e) => setFilters({...filters, date: e.target.value})}
                    />
                    <button style={styles.pageBtn} onClick={() => goToDay(1)}>Sledeći dan ›</button>
                </div>
                <div style={styles.rowInline}>
                    <select
                        style={styles.selectInput}
                        value={filters.doctorId}
                        onChange={(e) => setFilters({...filters, doctorId: e.target.value})}
                    >
                        {availableDoctors.map(d => <option key={d.id} value={d.id}>dr {d.firstName} {d.lastName}</option>)}
                    </select>
                    <button style={styles.changeDoctorBtn} onClick={() => setShowChangeDoctor(true)}>
                        Promeni doktora
                    </button>
                </div>
                <input
                    type="text"
                    placeholder="Brza pretraga (ime/telefon)..."
                    style={styles.searchInput}
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                />

                {searchTerm !== '' && (
                    <div style={styles.searchResults}>
                        {globalSearchResults.length === 0 && (
                            <div style={styles.searchResultEmpty}>Nema termina za "{filters.search}".</div>
                        )}
                        {globalSearchResults.map(app => (
                            <div key={app.id} style={styles.searchResultRow} onClick={() => jumpToResult(app)}>
                                <div>
                                    <div style={styles.pName}>{app.patientFullName}</div>
                                    <div style={styles.pSub}>{app.serviceName} • {app.doctorFullName}</div>
                                </div>
                                <div style={styles.searchResultWhen}>
                                    {formatDayLabel(app.startTime.split('T')[0])}<br/>{formatTime(app.startTime)}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <button
                    style={{ ...styles.pdfBtn, ...(downloadingPdf ? styles.pdfBtnDisabled : {}) }}
                    onClick={downloadPdf}
                    disabled={downloadingPdf}
                >
                    {downloadingPdf ? 'Priprema PDF-a...' : 'Preuzmi PDF sveske (po doktoru)'}
                </button>
            </div>

            {/* Sveska - jedna kolona na uskom ekranu, dve (izabrani dan + sledeći) na širokom */}
            <div className="sveska-columns">
                {renderDayColumn(filters.date, 'sveska-column')}
                {renderDayColumn(addDays(filters.date, 1), 'sveska-column sveska-column-next')}
            </div>

            <AppointmentGroupModal
                appointments={modalAppointments}
                onClose={() => setModalAppointments(null)}
                onRefresh={fetchData}
            />

            <ChangeDoctorModal
                open={showChangeDoctor}
                date={filters.date}
                sourceDoctorId={filters.doctorId}
                doctors={doctors}
                otherDoctors={otherDoctorsToday}
                kontrolaAppointments={kontrolaToday}
                onClose={() => setShowChangeDoctor(false)}
                onDone={fetchData}
            />
        </div>
    );
};

const styles = {
    container: { padding: '10px', backgroundColor: '#f1f5f9', minHeight: '100vh' },
    filterCard: {
        backgroundColor: '#fff', padding: '12px', borderRadius: '12px', marginBottom: '10px',
        display: 'flex', flexDirection: 'column', gap: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    },
    rowInline: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
    dateInput: { flex: '1 1 140px', minWidth: '140px', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' },
    selectInput: { flex: '1 1 140px', minWidth: '140px', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' },
    changeDoctorBtn: {
        flex: '1 1 140px', padding: '10px', borderRadius: '8px', border: '1px solid #c7d2fe',
        backgroundColor: '#eef2ff', color: '#4338ca', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
    },
    searchInput: { padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' },
    searchResults: {
        border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', maxHeight: '260px', overflowY: 'auto'
    },
    searchResultEmpty: { padding: '12px 14px', color: '#94a3b8', fontSize: '13px' },
    searchResultRow: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px',
        padding: '10px 14px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer'
    },
    searchResultWhen: { fontSize: '12px', color: '#64748b', textAlign: 'right', textTransform: 'capitalize', lineHeight: '1.4' },
    pageBtn: { flex: '1 1 auto', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#f1f5f9', color: '#334155', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
    pdfBtn: { padding: '10px', borderRadius: '8px', border: 'none', backgroundColor: '#8b5cf6', color: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    pdfBtnDisabled: { backgroundColor: '#c4b5fd', cursor: 'not-allowed' },

    column: { flex: 1, minWidth: 0 },
    columnHeader: {
        fontSize: '14px', fontWeight: '700', color: '#334155', marginBottom: '8px',
        textTransform: 'capitalize'
    },
    list: { backgroundColor: '#fff', borderRadius: '12px', overflow: 'hidden' },

    filledRow: {
        display: 'flex', alignItems: 'center', padding: '12px 15px',
        borderBottom: '1px solid #f1f5f9', backgroundColor: '#fff'
    },
    emptyRow: {
        display: 'flex', alignItems: 'center', padding: '12px 15px',
        borderBottom: '1px solid #f8fafc', backgroundColor: '#fafafa'
    },

    timeLabel: { fontSize: '14px', fontWeight: 'bold', color: '#64748b', minWidth: '50px' },
    patientInfo: { flex: 1, paddingLeft: '15px', borderLeft: '3px solid #1c8aa6', cursor: 'pointer' },
    pName: { fontSize: '15px', fontWeight: '600', color: '#1e293b', textTransform: 'capitalize' },
    pSub: { fontSize: '12px', color: '#64748b' },
    groupBadge: {
        marginLeft: '8px', backgroundColor: '#1c8aa6', color: 'white', borderRadius: '10px',
        padding: '1px 8px', fontSize: '11px', fontWeight: 'bold', verticalAlign: 'middle'
    },

    emptyInfo: {
        flex: 1, paddingLeft: '15px', color: '#cbd5e1', fontSize: '14px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: '5px'
    },
    occupiedInfo: {
        flex: 1, paddingLeft: '15px', borderLeft: '3px solid #fbbf24', color: '#b45309', fontSize: '13px'
    },
    plus: { fontSize: '18px', fontWeight: 'bold' },
    loader: { textAlign: 'center', marginTop: '50px' }
};

export default SveskaView;
